import { useState } from 'react';
import { discussionThread } from '@/mocks/home';
import SectionHeading from '@/pages/home/components/SectionHeading';
import Reveal from '@/components/base/Reveal';

const roleTabs = ['全部回复', '跟帖', '反驳', '补充线索'] as const;

const roleStyles: Record<string, string> = {
  跟帖: 'bg-secondary-100 text-secondary-900',
  反驳: 'bg-accent-100 text-accent-900',
  补充线索: 'bg-primary-100 text-primary-800',
};

export default function DiscussionSection() {
  const [activeRole, setActiveRole] = useState<(typeof roleTabs)[number]>('全部回复');
  const [draft, setDraft] = useState('');
  const [hint, setHint] = useState('');

  const replies = discussionThread.repliesList.filter(
    (reply) => activeRole === '全部回复' || reply.role === activeRole,
  );

  const handleCompose = () => {
    setHint(draft.trim() ? '演示页面：你的回复不会真正提交，正式版会先展示在讨论区顶部。' : '先写点内容再发送吧。');
  };

  return (
    <section id="discussion" className="w-full border-y border-background-200 bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1360px] px-4 md:px-6">
        <SectionHeading
          accent
          kicker="讨论区 · 跟帖 / 反驳 / 补充线索"
          title="一条帖子是怎么被聊透的"
          desc="同一件事，有人来跟帖、有人来反驳、有人默默补上关键线索。下面这条帖子还在持续更新，你可以先看看别人是怎么接话的。"
          actionLabel="进入讨论区"
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr] lg:gap-10">
          <div className="rounded-lg border border-background-200 bg-background-100">
            <div className="border-b border-background-200 px-5 py-5 md:px-6">
              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="rounded bg-primary-100 px-2 py-0.5 font-medium text-primary-800">正在热讨</span>
                <span>{discussionThread.time}</span>
                <span className="h-3 w-px bg-background-300" />
                <span>{discussionThread.views} 次围观</span>
                <span className="h-3 w-px bg-background-300" />
                <span>{discussionThread.replies} 条跟帖</span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                {discussionThread.title}
              </h3>
              <p className="mt-2 text-xs text-foreground-500">楼主 · {discussionThread.author}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-b border-background-200 px-5 py-3 md:px-6">
              {roleTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveRole(tab)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    activeRole === tab
                      ? 'border-secondary-500 bg-secondary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <ul className="divide-y divide-background-200">
              {replies.length === 0 ? (
                <li className="px-5 py-12 text-center text-sm text-foreground-500">这个类型下暂时还没有回复。</li>
              ) : (
                replies.map((reply, index) => (
                  <Reveal key={reply.id} delay={index * 60}>
                    <li className="flex gap-4 px-5 py-4 md:px-6">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-200 font-heading text-sm font-bold text-foreground-700">
                        {reply.author.slice(0, 1)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-foreground-900">{reply.author}</span>
                          <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${roleStyles[reply.role]}`}>
                            {reply.role}
                          </span>
                          <span className="text-[11px] text-foreground-400">{reply.time}</span>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{reply.content}</p>
                        <div className="mt-2 flex items-center gap-4 text-[11px] text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-thumb-up-line" />
                            {reply.likes}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-reply-line" />
                            回复
                          </span>
                        </div>
                      </div>
                    </li>
                  </Reveal>
                ))
              )}
            </ul>

            <div className="border-t border-background-200 px-5 py-5 md:px-6">
              <label htmlFor="discussion-draft" className="text-sm font-medium text-foreground-800">
                说点什么
              </label>
              <textarea
                id="discussion-draft"
                name="discussion_draft"
                maxLength={500}
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setHint('');
                }}
                rows={3}
                placeholder="补充一条线索，或者提出你的反驳……"
                className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {['跟帖', '反驳', '补充线索'].map((tag) => (
                    <span
                      key={tag}
                      className="rounded border border-background-300 bg-background-50 px-2 py-0.5 text-[11px] text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                  <span className="text-[11px] text-foreground-400">{draft.length}/500</span>
                </div>
                <button
                  type="button"
                  onClick={handleCompose}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-fill" />
                  发布回复
                </button>
              </div>
              {hint && (
                <p className="mt-3 flex items-start gap-1.5 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-900">
                  <i className="ri-information-line mt-0.5" />
                  {hint}
                </p>
              )}
            </div>
          </div>

          <Reveal className="lg:pt-1" delay={100}>
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">讨论区怎么用</h3>
                <ul className="mt-4 space-y-4">
                  {[
                    { icon: 'ri-chat-3-line', title: '跟帖', desc: '补充你的观察和体验，让帖子更完整。' },
                    { icon: 'ri-scales-3-line', title: '反驳', desc: '指出推断里跳步的地方，附带你的依据。' },
                    { icon: 'ri-search-eye-line', title: '补充线索', desc: '上传来源、时间与出处，让后来的人能查。' },
                  ].map((item) => (
                    <li key={item.title} className="flex gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                        <i className={`${item.icon} text-base`} />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{item.title}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">这条帖子的数据</h3>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {[
                    { label: '围观', value: discussionThread.views },
                    { label: '跟帖', value: discussionThread.replies },
                    { label: '补充线索', value: '216' },
                    { label: '被收藏', value: '4,830' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md bg-background-100 px-3 py-3">
                      <p className="font-heading text-lg font-bold text-foreground-950">{item.value}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}